import type { BondOrder } from '../domain/editor';
import { deletePathNodes, insertPathNode, nearestPathSegment } from '../domain/pathEditing';
import { calloutTargets, nearestCalloutTarget } from '../domain/calloutTargets';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject
} from 'react';
import type { DrawingLine, NormalizedPoint } from '../model';
import type { DrawingAtom } from '../domain/graphDrawing';
import type { EditorSelection, EditorTool } from '../domain/editor';
import { ESCAPE_PRIORITY, useEscapeAction } from './escapeArbiter';
import type { RenderableAnnotation } from '../domain/annotationRendering';
import type { PaperSize } from '../domain/geometry';
import type { ViewportTransform } from '../domain/viewport';
import {
  normalizedToPixel,
  distanceBetween,
  nearestAtomWithin,
  ATOM_ATTACH_DISTANCE_PX,
  type PixelPoint
} from '../domain/bonds';
import { nearestPoint, selectionAtPoint, uniqueEndpoints, usesTouchTarget } from '../domain/paperHitTesting';
import { createRingTemplatePlacement, type RingTemplate, type RingTemplatePlacement } from '../domain/ringTemplates';
import { createChainTemplatePlacement } from '../domain/chainTemplates';
import {
  boundsOf,
  canvasRings,
  editShape,
  insidePolygon,
  objectHandles,
  rotatePoint,
  samePosition,
  shapeHandles,
  pathNodeHandles,
  transformPoints,
  type BondAtomIds,
  type EditingBounds,
  type ObjectHandle,
  type PaperShape
} from '../domain/objectEditing';
import { NO_SNAP, snappedDragOffset, type SnapPoint } from '../domain/gridSnap';

type Event = ReactPointerEvent<HTMLDivElement>;
// Issue #424: how long the pointer must sit still mid-chain-drag before the segment drawn so
// far freezes and a new segment starts. Suggested range was 300-900ms; 450ms picked as a
// reasonable middle default -- easy to retune here if it feels off in practice.
const CHAIN_FREEZE_PAUSE_MS = 450;
interface Options {
  tool: EditorTool;
  atoms: readonly DrawingAtom[];
  lines: readonly DrawingLine[];
  annotations: readonly RenderableAnnotation[];
  selection: EditorSelection;
  onSelectionChange: (selection: EditorSelection) => void;
  onAtomsMove: (positions: Readonly<Record<string, NormalizedPoint>>) => void;
  onShapeChange: (shape: PaperShape) => void;
  // #756/#637 -- selected annotations dragged by a text block in Select mode: the paper-normalized
  // offset to move every selected annotation by.
  onAnnotationsMove?: (offset: NormalizedPoint) => void;
  onRingPlace: (lines: readonly DrawingLine[], template: RingTemplate, sharedBondId?: string) => void;
  onChainPlace: (lines: readonly DrawingLine[]) => void;
  bondAtomIds: BondAtomIds;
  // ADR 0014 -- the molecule an atom belongs to when it sits in a reaction box (else undefined):
  // a press on a boxed molecule selects all of it; pressing it again without moving narrows.
  boxedMolecule?: (atomId: string) => readonly string[] | undefined;
  bondOrders: Readonly<Record<string, BondOrder>>;
  editingBounds: EditingBounds;
  paperSize: PaperSize;
  zoom: number;
  ringTemplate: RingTemplate | null;
  bondLength: number;
  sizeRef: RefObject<PaperSize>;
  viewportRef: RefObject<ViewportTransform>;
  documentId?: string;
  // #772 part 2b -- Snap to grid (identity when off); attaching to atoms and bonds wins.
  snapPoint?: SnapPoint;
}
interface Drag {
  pointerId: number;
  element: HTMLDivElement;
  start: NormalizedPoint;
  // Where the pointer actually went down (start may be snapped to the grid, #772 part 2b).
  press: NormalizedPoint;
  mode: string;
  originalShape?: PaperShape;
  shape?: PaperShape;
  creating?: boolean;
  positions?: Record<string, NormalizedPoint>;
  initialPositions?: Record<string, NormalizedPoint>;
  ring?: RingTemplatePlacement;
  originalRing?: RingTemplatePlacement;
  template?: RingTemplate;
  chainStart?: NormalizedPoint;
  chain?: readonly DrawingLine[];
  frozenChain?: readonly DrawingLine[];
  chainAngleDegrees?: number;
  chainCurrent?: NormalizedPoint;
  /** Selection to narrow to if this press ends without moving (a 2nd click on a boxed molecule). */
  narrowTo?: EditorSelection;
  /** #756 -- text blocks being moved: their original and current top-left corners. */
  textOrigins?: Record<string, NormalizedPoint>;
  textPositions?: Record<string, NormalizedPoint>;
}
const offsetPoint = (point: NormalizedPoint, offset: NormalizedPoint): NormalizedPoint => ({
  x: point.x + offset.x,
  y: point.y + offset.y
});
// The point nearest the press (the item being grabbed); the press itself when there is none.
function nearestOf(press: NormalizedPoint, points: readonly NormalizedPoint[]): NormalizedPoint {
  let best = press;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const p of points) {
    const distance = Math.hypot(p.x - press.x, p.y - press.y);
    if (distance < bestDistance) {
      best = p;
      bestDistance = distance;
    }
  }
  return best;
}
export function usePaperObjects(options: Options) {
  const {
    tool,
    atoms,
    lines,
    annotations,
    selection,
    onSelectionChange,
    onAtomsMove,
    onShapeChange,
    onAnnotationsMove,
    onRingPlace,
    onChainPlace,
    bondAtomIds,
    boxedMolecule,
    bondOrders,
    editingBounds,
    paperSize,
    zoom,
    ringTemplate,
    bondLength,
    sizeRef,
    viewportRef,
    documentId,
    snapPoint = NO_SNAP
  } = options;
  const dragRef = useRef<Drag | null>(null);
  const [preview, setPreview] = useState<Drag | null>(null);
  const [notice, setNotice] = useState('');
  const noticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearNoticeTimer = useCallback(() => {
    if (noticeTimerRef.current !== null) {
      clearTimeout(noticeTimerRef.current);
      noticeTimerRef.current = null;
    }
  }, []);
  const setTransientNotice = useCallback(
    (msg: string, autoDismissMs = 4000) => {
      clearNoticeTimer();
      setNotice(msg);
      if (msg && autoDismissMs > 0) {
        noticeTimerRef.current = setTimeout(() => {
          setNotice('');
          noticeTimerRef.current = null;
        }, autoDismissMs);
      }
    },
    [clearNoticeTimer]
  );
  const [pathEdit, setPathEdit] = useState<{ id: string; nodes: number[] } | null>(null);
  const freezeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rings = useMemo(() => canvasRings(atoms, lines, bondAtomIds), [atoms, lines, bondAtomIds]);
  const pointerTargets = useMemo(
    () => calloutTargets(atoms, lines, rings, annotations),
    [atoms, lines, rings, annotations]
  );
  const atomPositions = useMemo(() => new Map(atoms.map((a) => [a.id, a.position])), [atoms]);
  const selectedIds = new Set([...selection.atomIds, ...selection.bondIds.flatMap((id) => bondAtomIds[id] ?? [])]);
  const selectedAtoms = atoms.filter((a) => selectedIds.has(a.id));
  const positions = preview?.positions;
  const selectedPoints = selectedAtoms.map((a) => positions?.[a.id] ?? a.position);
  const selectedShape = (preview?.shape ??
    annotations.find((a) => a.kind === 'shape' && selection.annotationIds?.includes(a.id))) as PaperShape | undefined;
  const editingPath =
    tool === 'select' && selectedShape?.shape === 'path' && pathEdit?.id === selectedShape.id
      ? selectedShape
      : undefined;
  const handles: ObjectHandle[] = selectedShape
    ? editingPath
      ? pathNodeHandles(editingPath, pathEdit?.nodes ?? [])
      : shapeHandles(selectedShape, paperSize, zoom)
    : selectedPoints.length > 1
      ? [
          ...objectHandles(selectedPoints, paperSize, zoom),
          ...selectedAtoms.map((a) => ({ id: `atom-${a.id}`, point: positions?.[a.id] ?? a.position }))
        ]
      : [];
  // #838 -- the selection box (as drawn: the selected atoms' extent plus 14 px, see
  // PaperVectorLayer's .selection-boundary): a press inside it, off anything else, moves the whole
  // selection, and the pointer shows an open hand there.
  const insideSelectionBox = (at: NormalizedPoint): boolean => {
    if (selectedPoints.length < 2) return false;
    const size = sizeRef.current;
    const pad = 14 / viewportRef.current.zoom;
    const xs = selectedPoints.map((p) => p.x * size.width);
    const ys = selectedPoints.map((p) => p.y * size.height);
    const x = at.x * size.width;
    const y = at.y * size.height;
    return (
      x >= Math.min(...xs) - pad &&
      x <= Math.max(...xs) + pad &&
      y >= Math.min(...ys) - pad &&
      y <= Math.max(...ys) + pad
    );
  };
  const point = (event: Pick<Event, 'currentTarget' | 'clientX' | 'clientY'>): NormalizedPoint => {
    const rect = event.currentTarget.getBoundingClientRect(),
      v = viewportRef.current,
      size = sizeRef.current;
    return {
      x: (event.clientX - rect.left - v.panX) / v.zoom / size.width,
      y: (event.clientY - rect.top - v.panY) / v.zoom / size.height
    };
  };
  const cancel = useCallback(() => {
    if (freezeTimerRef.current !== null) {
      clearTimeout(freezeTimerRef.current);
      freezeTimerRef.current = null;
    }
    clearNoticeTimer();
    setNotice('');
    const drag = dragRef.current;
    dragRef.current = null;
    setPreview(null);
    if (drag?.element.hasPointerCapture(drag.pointerId)) drag.element.releasePointerCapture(drag.pointerId);
  }, [clearNoticeTimer]);
  useEffect(() => {
    return () => {
      clearNoticeTimer();
    };
  }, [clearNoticeTimer]);
  useEffect(() => {
    // Release native pointer capture when the committed document or active tool changes.
    // eslint-disable-next-line react/set-state-in-effect
    cancel();
    return cancel;
  }, [tool, documentId, atoms, lines, annotations, cancel]);
  // #762 -- Escape cancels an unfinished object/ring/chain/text drag (escapeArbiter).
  useEscapeAction(ESCAPE_PRIORITY.gesture, () => {
    if (!dragRef.current) return false;
    cancel();
    return true;
  });
  useEscapeAction(ESCAPE_PRIORITY.group, () => {
    if (!editingPath) return false;
    setPathEdit(null);
    return true;
  });
  useEffect(() => {
    if (
      tool !== 'select' ||
      !annotations.some((a) => a.id === pathEdit?.id) ||
      !selection.annotationIds?.includes(pathEdit?.id ?? '')
    ) {
      setPathEdit(null);
    }
  }, [tool, documentId, annotations, selection.annotationIds, pathEdit?.id]);
  // Capture Delete before the workspace's whole-object command. Other fields keep their keys.
  useEffect(() => {
    if (!editingPath) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing || (event.key !== 'Delete' && event.key !== 'Backspace')) return;
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('input,textarea,select,[contenteditable="true"],[role="dialog"],[aria-modal="true"]')
      )
        return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (dragRef.current) return;
      const next = deletePathNodes(editingPath, new Set(pathEdit?.nodes));
      if (next !== editingPath) {
        onShapeChange(next);
        setPathEdit({ id: editingPath.id, nodes: [] });
        clearNoticeTimer();
        setNotice('');
      } else if (pathEdit?.nodes.length)
        setTransientNotice(
          `Keep at least ${editingPath.closed ? 3 : 2} nodes in this ${editingPath.closed ? 'closed' : 'open'} path.`
        );
    };
    globalThis.addEventListener('keydown', onKey, true);
    return () => globalThis.removeEventListener('keydown', onKey, true);
  }, [editingPath, pathEdit, onShapeChange, clearNoticeTimer, setTransientNotice]);
  const doubleClick = (event: React.MouseEvent<HTMLDivElement>): boolean => {
    if (tool !== 'select') return false;
    const at = point(event),
      size = sizeRef.current,
      v = viewportRef.current;
    const hit = [...annotations]
      .reverse()
      .find(
        (a) => a.kind === 'shape' && a.shape === 'path' && nearestPathSegment(a, at, size).distance * v.zoom <= 12
      ) as PaperShape | undefined;
    if (!hit) return false;
    event.preventDefault();
    event.currentTarget.focus({ preventScroll: true });
    if (!editingPath || editingPath.id !== hit.id) {
      onSelectionChange({ atomIds: [], bondIds: [], annotationIds: [hit.id] });
      setPathEdit({ id: hit.id, nodes: [] });
      clearNoticeTimer();
      setNotice('');
      return true;
    }
    const nearest = nearestPathSegment(hit, at, size);
    if (nearest.distance * v.zoom <= 12 && nearest.t > 0.001 && nearest.t < 0.999) {
      onShapeChange(insertPathNode(hit, nearest.index, nearest.t));
      setPathEdit({ id: hit.id, nodes: [nearest.index + 1] });
      clearNoticeTimer();
      setNotice('');
    }
    return true;
  };
  const begin = (event: Event, data: Omit<Drag, 'pointerId' | 'element' | 'start' | 'press'>, start = point(event)) => {
    event.preventDefault();
    const drag = { ...data, pointerId: event.pointerId, element: event.currentTarget, start, press: point(event) };
    dragRef.current = drag;
    setPreview(drag);
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const beginAtoms = (event: Event, ids: readonly string[], mode: string) => {
    const initialPositions = Object.fromEntries(atoms.filter((a) => ids.includes(a.id)).map((a) => [a.id, a.position]));
    begin(event, { initialPositions, positions: initialPositions, mode });
  };
  const linesWithinBounds = (candidates: readonly DrawingLine[]) =>
    candidates.every((l) =>
      [l.start, l.end].every(
        (p) =>
          p.x >= editingBounds.minX &&
          p.x <= editingBounds.maxX &&
          p.y >= editingBounds.minY &&
          p.y <= editingBounds.maxY
      )
    );
  // #451: releasing (or just hovering, mid-drag) near an existing atom -- from the committed
  // structure or an earlier frozen segment of this same gesture -- connects the chain's last bond
  // to it (closing a ring/branch) instead of leaving a new, separate atom next to it. Mirrors the
  // plain single-bond draw tool's existing-endpoint snap in usePaperPointer.ts.
  const connectLastBondToNearbyAtom = (
    placementLines: readonly DrawingLine[],
    rawPixel: PixelPoint,
    candidateAtoms: readonly DrawingAtom[],
    candidateLines: readonly DrawingLine[],
    size: PaperSize
  ): readonly DrawingLine[] => {
    const last = placementLines.at(-1);
    if (!last) return placementLines;
    const target = nearestPoint(
      rawPixel,
      uniqueEndpoints(candidateLines, candidateAtoms, size),
      ATOM_ATTACH_DISTANCE_PX
    );
    if (!target) return placementLines;
    const targetNormalized = { x: target.x / size.width, y: target.y / size.height };
    if (samePosition(targetNormalized, last.start)) return placementLines;
    return placementLines.map((l, i) => (i === placementLines.length - 1 ? { ...l, end: targetNormalized } : l));
  };
  // Issue #424: a pause mid-chain-drag locks the segment drawn so far in place -- the next
  // segment re-anchors from its endpoint, so one continuous gesture can bend in a new direction
  // instead of only ever following a single straight trend line.
  const freezeChainSegment = () => {
    const drag = dragRef.current;
    if (!drag || drag.chainStart === undefined || !drag.chain?.length) return;
    const last = drag.chain[drag.chain.length - 1]!;
    const next: Drag = {
      ...drag,
      frozenChain: [...(drag.frozenChain ?? []), ...drag.chain],
      chainStart: last.end,
      chain: []
    };
    dragRef.current = next;
    setPreview(next);
  };
  const scheduleFreezeTimer = () => {
    if (freezeTimerRef.current !== null) clearTimeout(freezeTimerRef.current);
    freezeTimerRef.current = globalThis.setTimeout(freezeChainSegment, CHAIN_FREEZE_PAUSE_MS);
  };
  const pointerDown = (event: Event): boolean => {
    if (
      event.button !== 0 ||
      (tool !== 'select' &&
        tool !== 'chain' &&
        !tool.startsWith('shape-') &&
        !tool.startsWith('callout-') &&
        !ringTemplate)
    )
      return false;
    if (dragRef.current) return true;
    clearNoticeTimer();
    setNotice('');
    const at = point(event),
      pixel = normalizedToPixel(at, sizeRef.current);
    const radius = (usesTouchTarget(event.pointerType) ? 22 : 10) / viewportRef.current.zoom;
    // The chain tool starts (and, on an existing atom, continues) a zigzag from a press-drag-release
    // gesture -- it never hit-tests handles or existing geometry the way select/ring/shape do.
    // #772 part 2b -- a chain, ring or shape started away from any atom starts on the grid.
    const onAtom = nearestAtomWithin(pixel, atoms, sizeRef.current, ATOM_ATTACH_DISTANCE_PX) !== undefined;
    const gridAt = onAtom ? at : snapPoint(at, event);
    if (tool === 'chain') {
      const chainAt = gridAt;
      const placement = createChainTemplatePlacement(
        chainAt,
        chainAt,
        bondLength,
        sizeRef.current,
        atoms,
        lines,
        bondOrders
      );
      if (!linesWithinBounds(placement.lines)) {
        setTransientNotice('The chain would extend beyond the drawing bounds. Place it farther inside the document.');
        return true;
      }
      begin(event, { chainStart: chainAt, chain: placement.lines, mode: 'chain', creating: true }, chainAt);
      scheduleFreezeTimer();
      return true;
    }
    // Endpoints take precedence over the group's move handle for short bonds. #838: for a molecule
    // the centre move handle isn't drawn and the whole selection box moves the selection, so the
    // handle no longer outranks a bond (or atom) under it -- a drag on a bond moves that bond.
    const molecular = handles.some((h) => h.id.startsWith('atom-'));
    const handle = [...handles]
      .filter((h) => !(molecular && h.id === 'move'))
      .sort((a, b) => Number(b.id.startsWith('atom-')) - Number(a.id.startsWith('atom-')))
      .find((h) => distanceBetween(pixel, normalizedToPixel(h.point, sizeRef.current)) <= radius);
    if (
      handle &&
      (tool === 'select' || (selectedShape && (tool.startsWith('shape-') || tool.startsWith('callout-'))))
    ) {
      if (event.shiftKey && handle.id.startsWith('atom-')) return false;
      // ADR 0014 -- an atom of a fully selected boxed molecule: dragging moves the whole selection
      // (not just this endpoint); a press without moving narrows the selection to the atom.
      const handleAtomId = handle.id.startsWith('atom-') ? handle.id.slice('atom-'.length) : undefined;
      const boxed = handleAtomId ? boxedMolecule?.(handleAtomId) : undefined;
      if (handleAtomId && boxed?.every((id) => selection.atomIds.includes(id))) {
        beginAtoms(event, [...selectedIds], 'move');
        dragRef.current!.narrowTo = { atomIds: [handleAtomId], bondIds: [] };
        return true;
      }
      if (editingPath && handle.id.startsWith('point-')) {
        const index = Number(handle.id.slice(6));
        const nodes = pathEdit?.nodes ?? [];
        setPathEdit({
          id: editingPath.id,
          nodes: event.shiftKey
            ? nodes.includes(index)
              ? nodes.filter((i) => i !== index)
              : [...nodes, index]
            : [index]
        });
        if (event.shiftKey) {
          event.preventDefault();
          return true;
        }
      }
      if (selectedShape) begin(event, { originalShape: selectedShape, shape: selectedShape, mode: handle.id });
      else beginAtoms(event, [...selectedIds], handle.id);
      return true;
    }
    if (editingPath) {
      const onPath = nearestPathSegment(editingPath, at, sizeRef.current).distance <= radius;
      if (onPath) {
        event.preventDefault();
        return true;
      }
      setPathEdit(null);
    }
    if (ringTemplate) {
      const attached = createRingTemplatePlacement(at, ringTemplate, bondLength, sizeRef.current, atoms, lines);
      // Fused to a bond or attached to an atom: placed by the chemistry, not the grid.
      const free = !attached.sharedBondId && !onAtom;
      const ringAt = free ? gridAt : at;
      const ring = free
        ? createRingTemplatePlacement(ringAt, ringTemplate, bondLength, sizeRef.current, atoms, lines)
        : attached;
      if (!linesWithinBounds(ring.lines)) {
        setTransientNotice('The ring would extend beyond the drawing bounds. Place it farther inside the document.');
        return true;
      }
      begin(event, { ring, originalRing: ring, template: ringTemplate, mode: 'ring', creating: true }, ringAt);
      setNotice(
        ring.sharedBondId
          ? 'Fused ring: the shared bond fixes size and orientation.'
          : 'Drag to size and orient the ring.'
      );
      return true;
    }
    if (tool.startsWith('shape-') || tool.startsWith('callout-')) {
      const shape: PaperShape = {
        id: crypto.randomUUID(),
        kind: 'shape',
        shape: tool.startsWith('callout-') ? 'rect' : (tool.slice(6) as PaperShape['shape']),
        ...(tool.startsWith('callout-')
          ? {
              callout: {
                variant: tool.slice(8) as NonNullable<PaperShape['callout']>['variant'],
                tip: { x: gridAt.x - 0.04, y: gridAt.y + 0.12 },
                paragraphs: [{ runs: [] }] as const
              }
            }
          : {}),
        points: [gridAt, gridAt]
      };
      begin(event, { originalShape: shape, shape, mode: 'create', creating: true }, gridAt);
      return true;
    }
    // #756 -- text sits above the structure: a press on a block selects it (Shift toggles it in an
    // annotation selection) and drags every selected block. Hit by position on the block's hit
    // rect, which only Select/Text modes make hittable.
    if (tool === 'select' && onAnnotationsMove) {
      const hitId = event.currentTarget.ownerDocument
        .elementsFromPoint(event.clientX, event.clientY)
        .find((element) => element.classList.contains('text-annotation-hit'))
        ?.closest('[data-annotation-id]')
        ?.getAttribute('data-annotation-id');
      const text = hitId ? annotations.find((a) => a.id === hitId && a.kind === 'text') : undefined;
      if (text) {
        const current = selection.annotationIds ?? [];
        if (event.shiftKey) {
          const annotationIds = current.includes(text.id)
            ? current.filter((id) => id !== text.id)
            : [...current, text.id];
          onSelectionChange({ ...selection, annotationIds });
          event.preventDefault();
          return true;
        }
        // #637 -- a selected block in a selection with structure moves the whole selection.
        if (current.includes(text.id) && selectedIds.size > 0) {
          beginAtoms(event, [...selectedIds], 'move');
          return true;
        }
        const ids = current.includes(text.id) ? current : [text.id];
        if (!current.includes(text.id)) onSelectionChange({ atomIds: [], bondIds: [], annotationIds: ids });
        const textOrigins = Object.fromEntries(
          annotations.flatMap((a) => (a.kind === 'text' && ids.includes(a.id) ? [[a.id, a.position]] : []))
        );
        begin(event, { mode: 'text-move', textOrigins, textPositions: textOrigins });
        return true;
      }
    }
    const graphHit = selectionAtPoint(pixel, atoms, lines, sizeRef.current, radius);
    if (graphHit?.kind === 'atom') return false;
    // ADR 0014 -- progressive selection inside a reaction box: a press on a bond or inside a ring of a
    // boxed molecule selects the whole molecule (and drags it); pressing a bond again narrows to it
    // on release.
    const boxedAtomId =
      graphHit?.kind === 'bond'
        ? bondAtomIds[graphHit.id]?.[0]
        : rings.find((ring) =>
            insidePolygon(
              at,
              ring.atomIds.map((id) => atoms.find((atom) => atom.id === id)!.position)
            )
          )?.atomIds[0];
    const molecule = boxedAtomId && !event.shiftKey ? boxedMolecule?.(boxedAtomId) : undefined;
    if (molecule) {
      const whole = molecule.every((id) => selection.atomIds.includes(id));
      if (!whole) {
        const inMolecule = new Set(molecule);
        const bondIds = Object.entries(bondAtomIds)
          .filter(([, ends]) => ends.every((id) => inMolecule.has(id)))
          .map(([id]) => id);
        onSelectionChange({ atomIds: [...molecule], bondIds });
        beginAtoms(event, molecule, 'move');
        return true;
      }
      // Whole molecule already selected: a bond narrows to itself on release; a press inside a
      // ring keeps the whole molecule (owner, #700: only an atom or a bond narrows, never a ring).
      beginAtoms(event, [...selectedIds], 'move');
      if (graphHit?.kind === 'bond') dragRef.current!.narrowTo = { atomIds: [], bondIds: [graphHit.id] };
      return true;
    }
    if (graphHit?.kind === 'bond') {
      if (event.shiftKey) return false;
      // #838 (owner, #836: "over a bond ... move an atom or a bond") -- a drag on a bond moves
      // that bond, even when it is part of a larger selection; the whole selection moves from
      // inside its box instead. (It used to move everything selected.)
      const bondIds = [graphHit.id];
      const atomIds: string[] = [];
      // #669: select only the bond -- the drag still moves its end atoms, but they stay out of the
      // selection so Delete removes just the bond and the bond controls act on the clicked bond.
      const ids = [...new Set([...atomIds, ...bondIds.flatMap((id) => bondAtomIds[id] ?? [])])];
      onSelectionChange({ atomIds, bondIds });
      beginAtoms(event, ids, 'move');
      return true;
    }
    // Test the actual rendered shape (including rotation) in local SVG coordinates.
    const shapes = [
      ...event.currentTarget.querySelectorAll<SVGGeometryElement>('.annotation-layer [data-annotation-id]')
    ].reverse();
    for (const element of shapes) {
      const id = element.getAttribute('data-annotation-id');
      const shape = annotations.find((a) => a.id === id && a.kind === 'shape') as PaperShape | undefined;
      const matrix = element.getScreenCTM();
      if (!shape || !matrix) continue;
      const local = new DOMPoint(event.clientX, event.clientY).matrixTransform(matrix.inverse());
      const box = element.getBBox();
      if (
        local.x < box.x - radius ||
        local.x > box.x + box.width + radius ||
        local.y < box.y - radius ||
        local.y > box.y + box.height + radius
      )
        continue;
      let hit = shape.shape !== 'path' && element.isPointInFill(local);
      const length = element.getTotalLength(),
        steps = Math.min(2048, Math.max(1, Math.ceil(length / (radius / 2))));
      for (let i = 0; !hit && i <= steps; i++)
        hit = distanceBetween(local, element.getPointAtLength((length * i) / steps)) <= radius;
      if (hit) {
        onSelectionChange({ atomIds: [], bondIds: [], annotationIds: [shape.id] });
        begin(event, { originalShape: shape, shape, mode: 'move' });
        return true;
      }
    }
    const ring = rings.find((r) =>
      insidePolygon(
        at,
        r.atomIds.map((id) => atoms.find((a) => a.id === id)!.position)
      )
    );
    // #838 -- inside the selection box, on nothing else (or inside a ring that is all selected):
    // move the whole selection, as the open-hand pointer there says.
    if (
      tool === 'select' &&
      !event.shiftKey &&
      insideSelectionBox(at) &&
      (!ring || ring.atomIds.every((id) => selectedIds.has(id)))
    ) {
      beginAtoms(event, [...selectedIds], 'move');
      return true;
    }
    if (ring) {
      onSelectionChange(ring);
      beginAtoms(event, ring.atomIds, 'move');
      setTransientNotice('Ring selected. Shared atoms move with this ring; attached bonds stay connected.');
      return true;
    }
    clearNoticeTimer();
    setNotice('');
    return false;
  };
  const updated = (drag: Drag, event: Event): Drag => {
    const at = point(event),
      size = sizeRef.current;
    if (drag.textOrigins) {
      const { x: dx, y: dy } = snappedDragOffset(
        Object.values(drag.textOrigins)[0] ?? drag.start,
        { x: at.x - drag.start.x, y: at.y - drag.start.y },
        snapPoint,
        { altKey: event.altKey, exclude: new Set(Object.keys(drag.textOrigins)) }
      );
      return {
        ...drag,
        textPositions: Object.fromEntries(
          Object.entries(drag.textOrigins).map(([id, origin]) => [id, { x: origin.x + dx, y: origin.y + dy }])
        )
      };
    }
    if (drag.originalShape?.callout && drag.mode === 'pointer-tip') {
      const shape = drag.originalShape;
      const bubble = shape.callout!;
      // A press without movement must never sever or retarget an existing attachment.
      if (
        Math.hypot((at.x - drag.press.x) * size.width, (at.y - drag.press.y) * size.height) * viewportRef.current.zoom <
        3
      )
        return { ...drag, shape: drag.originalShape };
      let snapped = nearestCalloutTarget(
        pointerTargets,
        at,
        size,
        (usesTouchTarget(event.pointerType) ? 24 : 16) / viewportRef.current.zoom
      );
      if (!snapped) {
        const hit = selectionAtPoint(normalizedToPixel(at, size), atoms, lines, size, 12 / viewportRef.current.zoom);
        if (hit)
          snapped = pointerTargets.find(
            (target) =>
              target.kind === hit.kind &&
              (target.attachment.kind === 'atom'
                ? target.attachment.atomId === hit.id
                : target.attachment.kind === 'bond' && target.attachment.bondId === hit.id)
          );
      }
      if (!snapped) {
        for (const target of pointerTargets.filter((target) => target.kind === 'shape')) {
          const elements = event.currentTarget.querySelectorAll<SVGGeometryElement>(
            '.annotation-layer [data-annotation-id]'
          );
          for (const element of elements) {
            if (
              target.attachment.kind !== 'annotation' ||
              element.getAttribute('data-annotation-id') !== target.attachment.annotationId ||
              !element.getScreenCTM()
            )
              continue;
            const local = new DOMPoint(event.clientX, event.clientY).matrixTransform(element.getScreenCTM()!.inverse());
            if (element.isPointInFill(local) || element.isPointInStroke(local)) snapped = target;
          }
        }
      }
      const attachment = snapped?.attachment;
      const { attachment: _previous, ...free } = bubble;
      return {
        ...drag,
        shape: { ...shape, callout: { ...free, tip: snapped?.point ?? at, ...(attachment ? { attachment } : {}) } }
      };
    }
    if (drag.originalShape) {
      if (
        !drag.creating &&
        Math.hypot((at.x - drag.press.x) * size.width, (at.y - drag.press.y) * size.height) * viewportRef.current.zoom <
          3
      )
        return { ...drag, shape: drag.originalShape };
      // #772 parts 2b/3 -- a corner or end snaps; a moved shape snaps its first point.
      const shapeSnap = { altKey: event.altKey, exclude: new Set([drag.originalShape.id]) };
      const target =
        drag.mode === 'create' || drag.mode.startsWith('point-')
          ? snapPoint(at, shapeSnap)
          : drag.mode === 'move'
            ? offsetPoint(
                drag.start,
                snappedDragOffset(
                  drag.originalShape.points[0] ?? drag.start,
                  { x: at.x - drag.start.x, y: at.y - drag.start.y },
                  snapPoint,
                  shapeSnap
                )
              )
            : at;
      const shape =
        drag.mode === 'create'
          ? {
              ...drag.originalShape,
              points: [drag.start, target],
              ...(drag.originalShape.callout
                ? {
                    callout: {
                      ...drag.originalShape.callout,
                      tip:
                        drag.originalShape.callout.variant === 'leader'
                          ? {
                              x: Math.max(drag.start.x, target.x) + 50 / size.width,
                              y: Math.min(drag.start.y, target.y) - 20 / size.height
                            }
                          : ['chevron', 'notched-arrow'].includes(drag.originalShape.callout.variant)
                            ? {
                                x: Math.max(drag.start.x, target.x) + 35 / size.width,
                                y: (drag.start.y + target.y) / 2
                              }
                            : {
                                x: Math.min(drag.start.x, target.x) + Math.abs(target.x - drag.start.x) * 0.2,
                                y: Math.max(drag.start.y, target.y) + 35 / size.height
                              }
                    }
                  }
                : {})
            }
          : editShape(drag.originalShape, drag.start, target, drag.mode, size, event.shiftKey);
      return { ...drag, shape };
    }
    if (drag.originalRing && drag.template) {
      if (drag.originalRing.sharedBondId) return drag;
      const points = drag.originalRing.lines.map((l) => l.start);
      const anchor = nearestAtomWithin(normalizedToPixel(drag.start, size), atoms, size, ATOM_ATTACH_DISTANCE_PX);
      const center = anchor?.position ?? drag.start;
      const dx = (at.x - center.x) * size.width,
        dy = (at.y - center.y) * size.height;
      // A press that hasn't moved places the ring as previewed. Measured from the press, not the
      // centre: a grid-snapped centre sits up to half a grid step from the pointer, which read as a
      // drag and rotated and shrank every clicked ring (#804 review).
      const movedPx = Math.hypot((at.x - drag.press.x) * size.width, (at.y - drag.press.y) * size.height);
      if (movedPx * viewportRef.current.zoom < 4) return { ...drag, ring: drag.originalRing };
      const baseCenter = boundsOf(points).center;
      const reference = anchor ? baseCenter : points[0]!;
      const baseAngle = Math.atan2((reference.y - center.y) * size.height, (reference.x - center.x) * size.width);
      let angle = Math.atan2(dy, dx) - baseAngle;
      if (event.shiftKey) angle = (Math.round(angle / (Math.PI / 12)) * Math.PI) / 12;
      const radius = bondLength / (2 * Math.sin(Math.PI / drag.template.size));
      const scale = Math.max(0.4, Math.hypot(dx, dy) / radius);
      const transform = (p: NormalizedPoint) =>
        rotatePoint(
          { x: center.x + (p.x - center.x) * scale, y: center.y + (p.y - center.y) * scale },
          center,
          angle,
          size
        );
      const next = drag.originalRing.lines.map((l) => ({ ...l, start: transform(l.start), end: transform(l.end) }));
      // #449: the pointerDown check above only catches an out-of-bounds placement at the very
      // start of the drag -- resizing/rotating a ring already in progress needs its own notice,
      // or dragging it past the edge mid-gesture rejects silently with no feedback at all.
      if (!linesWithinBounds(next)) {
        setNotice('The ring would extend beyond the drawing bounds. Place it farther inside the document.');
        return drag;
      }
      setNotice('');
      return { ...drag, ring: { lines: next } };
    }
    if (drag.chainStart) {
      // A frozen segment isn't in the committed graph yet, so give it synthetic atoms/lines --
      // matching DrawingPaper.tsx's preview-atom shape -- so the next segment's continuation
      // angle (connectedBondAngle) and bounds-check see it exactly as it'll look once committed.
      const frozen = drag.frozenChain ?? [];
      const frozenAtoms = frozen.flatMap((l) => [
        {
          id: `${l.id}-start`,
          position: l.start,
          atomicNumber: 6,
          formalCharge: 0,
          implicitHydrogenCount: 0,
          hasOpenValence: false
        },
        {
          id: `${l.id}-end`,
          position: l.end,
          atomicNumber: 6,
          formalCharge: 0,
          implicitHydrogenCount: 0,
          hasOpenValence: false
        }
      ]);
      const combinedAtoms = [...atoms, ...frozenAtoms];
      const combinedLines = [...lines, ...frozen];
      const anchorPixel = normalizedToPixel(drag.chainStart, size);
      const rawPixel = normalizedToPixel(at, size);
      const dragDistance = distanceBetween(anchorPixel, rawPixel);
      // #451: below a dead zone (a third of a standard bond length), don't redetermine direction
      // at all -- angle is hypersensitive to noise at small magnitudes, so a couple of pixels of
      // hand tremor right after a freeze can otherwise swing it by dozens of degrees. Keep
      // showing whatever was there (nothing, right after a freeze) rather than jitter. Mirrors
      // the ring-resize dead zone above.
      if (dragDistance < bondLength / 3) return drag;
      const rawAngleDegrees = (Math.atan2(rawPixel.y - anchorPixel.y, rawPixel.x - anchorPixel.x) * 180) / Math.PI;
      // #451: snap the chain's own drawing angle -- 5 degrees by default, 1 degree with Ctrl
      // (finer control once a chain is long enough that a 5-degree step sways its far end
      // visibly), free with Alt (which wins if both are held). Distinct from (and independent
      // of) the existing 15-degree Shift-snap used for rotate/orient gestures elsewhere -- this
      // snaps a *drawing* angle, not a rotation, and inverts the modifier (snapped by default, a
      // held key frees it).
      const snapStepDegrees = event.ctrlKey ? 1 : 5;
      const angleDegrees = event.altKey
        ? rawAngleDegrees
        : Math.round(rawAngleDegrees / snapStepDegrees) * snapStepDegrees;
      const angleRadians = (angleDegrees * Math.PI) / 180;
      const snappedAt = {
        x: (anchorPixel.x + dragDistance * Math.cos(angleRadians)) / size.width,
        y: (anchorPixel.y + dragDistance * Math.sin(angleRadians)) / size.height
      };
      const placement = createChainTemplatePlacement(
        drag.chainStart,
        snappedAt,
        bondLength,
        size,
        combinedAtoms,
        combinedLines,
        bondOrders
      );
      // #449: the pointerDown check above only catches an out-of-bounds placement at the very
      // start of the drag -- a chain that starts in bounds and is then dragged past the edge
      // needs its own notice, or it rejects silently with no feedback at all.
      if (!linesWithinBounds(placement.lines)) {
        setNotice('The chain would extend beyond the drawing bounds. Place it farther inside the document.');
        return drag;
      }
      setNotice('');
      const chain = connectLastBondToNearbyAtom(placement.lines, rawPixel, combinedAtoms, combinedLines, size);
      return { ...drag, chain, chainAngleDegrees: angleDegrees, chainCurrent: at };
    }
    if (drag.initialPositions) {
      const entries = Object.entries(drag.initialPositions);
      // A press that hasn't moved (under 4 screen px, as for rings above) leaves a moved group where
      // it is: otherwise snapping (a smart guide within 8 px) shifts it on a plain click -- which
      // also cancelled the narrowing second click on a boxed molecule (#753, ADR 0014).
      if (drag.mode === 'move') {
        const size = sizeRef.current;
        const movedPx = Math.hypot((at.x - drag.start.x) * size.width, (at.y - drag.start.y) * size.height);
        if (movedPx * viewportRef.current.zoom < 4) return { ...drag, positions: drag.initialPositions };
      }
      // #772 part 2b -- a dragged endpoint lands on the grid; a moved group moves so the atom
      // nearest the press does, the rest rigidly with it. Rotate and scale handles stay free.
      const atomSnap = { altKey: event.altKey, exclude: new Set(entries.map(([id]) => id)) };
      const snappedAt = drag.mode.startsWith('atom-')
        ? snapPoint(at, atomSnap)
        : drag.mode === 'move'
          ? offsetPoint(
              drag.start,
              snappedDragOffset(
                nearestOf(
                  drag.start,
                  entries.map(([, p]) => p)
                ),
                { x: at.x - drag.start.x, y: at.y - drag.start.y },
                snapPoint,
                atomSnap
              )
            )
          : at;
      const points = drag.mode.startsWith('atom-')
        ? entries.map(([id, p]) =>
            id === drag.mode.slice(5)
              ? {
                  x: Math.max(editingBounds.minX, Math.min(editingBounds.maxX, snappedAt.x)),
                  y: Math.max(editingBounds.minY, Math.min(editingBounds.maxY, snappedAt.y))
                }
              : p
          )
        : transformPoints(
            entries.map(([, p]) => p),
            drag.start,
            snappedAt,
            drag.mode,
            size,
            event.shiftKey,
            editingBounds
          );
      const next = Object.fromEntries(entries.map(([id], i) => [id, points[i]!]));
      // Reject collapsed connected bonds rather than letting depiction edits merge atoms.
      const bad = lines.some((l) => {
        const ids = bondAtomIds[l.id];
        if (!ids) return false;
        const a = next[ids[0]] ?? atomPositions.get(ids[0]);
        const b = next[ids[1]] ?? atomPositions.get(ids[1]);
        return a && b ? Math.hypot((a.x - b.x) * size.width, (a.y - b.y) * size.height) < 2 : false;
      });
      if (bad) setTransientNotice('That edit would collapse a bond. Move the handle farther from its connected atom.');
      return bad ? drag : { ...drag, positions: next };
    }
    return drag;
  };
  const pointerMove = (event: Event): boolean => {
    const drag = dragRef.current;
    if (!drag) return false;
    if (drag.pointerId !== event.pointerId) return true;
    event.preventDefault();
    const next = updated(drag, event);
    dragRef.current = next;
    setPreview(next);
    if (next.chainStart !== undefined) scheduleFreezeTimer();
    return true;
  };
  const finish = (event: Event, cancelled: boolean): boolean => {
    const drag = dragRef.current;
    if (!drag) return false;
    if (drag.pointerId !== event.pointerId) return true;
    const next = cancelled ? null : updated(drag, event);
    cancel();
    if (!next) return true;
    if (next.textPositions && next.textOrigins) {
      // A press without movement only selects.
      const origins = next.textOrigins;
      const moved = Object.entries(next.textPositions).some(
        ([id, p]) =>
          distanceBetween(normalizedToPixel(p, sizeRef.current), normalizedToPixel(origins[id]!, sizeRef.current)) *
            viewportRef.current.zoom >=
          2
      );
      const [id, position] = Object.entries(next.textPositions)[0] ?? [];
      if (moved && id && position) {
        onAnnotationsMove?.({ x: position.x - origins[id]!.x, y: position.y - origins[id]!.y });
      }
      return true;
    }
    if (next.shape) {
      const b = boundsOf(next.shape.points),
        size = sizeRef.current;
      if (
        next.creating &&
        (next.shape.shape === 'path'
          ? Math.hypot((b.maxX - b.minX) * size.width, (b.maxY - b.minY) * size.height) * viewportRef.current.zoom < 4
          : Math.abs(b.maxX - b.minX) * size.width * viewportRef.current.zoom < 4 ||
            Math.abs(b.maxY - b.minY) * size.height * viewportRef.current.zoom < 4)
      )
        return true;
      if (next.creating || JSON.stringify(next.shape) !== JSON.stringify(next.originalShape)) onShapeChange(next.shape);
    } else if (next.ring && next.template) onRingPlace(next.ring.lines, next.template, next.ring.sharedBondId);
    else if (next.chain?.length || next.frozenChain?.length)
      onChainPlace([...(next.frozenChain ?? []), ...(next.chain ?? [])]);
    else if (next.positions && JSON.stringify(next.positions) !== JSON.stringify(next.initialPositions))
      onAtomsMove(next.positions);
    else if (next.narrowTo) onSelectionChange(next.narrowTo);
    return true;
  };
  /**
   * #838 -- what a press at the pointer would grab, for the pointer's shape (mouse and pen):
   * 'rotate' / 'scale' / 'resize' handles; 'item' -- an atom or bond (the pointing finger);
   * 'selection' -- inside the selection box (the open hand); null -- empty paper.
   */
  const hoverTarget = (event: Event): 'rotate' | 'scale' | 'resize' | 'item' | 'selection' | null => {
    if (tool !== 'select') return null;
    const at = point(event);
    const pixel = normalizedToPixel(at, sizeRef.current);
    const radius = 10 / viewportRef.current.zoom;
    const handle = handles.find((h) => distanceBetween(pixel, normalizedToPixel(h.point, sizeRef.current)) <= radius);
    if (handle?.id === 'rotate') return 'rotate';
    if (handle?.id === 'scale') return 'scale';
    if (handle?.id === 'pointer-tip') return 'item';
    if (handle && (handle.id === 'resize' || handle.id.startsWith('point-') || handle.id.startsWith('control-')))
      return 'resize';
    if (selectionAtPoint(pixel, atoms, lines, sizeRef.current, radius)) return 'item';
    return insideSelectionBox(at) ? 'selection' : null;
  };
  return {
    hasActiveGesture: () => dragRef.current !== null,
    /** #838 -- the active gesture's mode (e.g. 'move'), for the closed-hand pointer while moving. */
    activeMode: () => dragRef.current?.mode ?? null,
    hoverTarget,
    doubleClick,
    cancel,
    pointerDown,
    pointerMove,
    finish,
    positions,
    handles,
    calloutTargets: preview?.mode === 'pointer-tip' ? pointerTargets : [],
    activeCalloutTarget:
      preview?.mode === 'pointer-tip'
        ? pointerTargets.find(
            (target) => JSON.stringify(target.attachment) === JSON.stringify(preview.shape?.callout?.attachment)
          )?.id
        : undefined,
    notice: editingPath
      ? notice ||
        'Path nodes: click a node for curve handles; Shift-click selects more. Double-click a segment to insert a node. Delete removes selected nodes. Escape cancels a drag, then exits node editing.'
      : selectedShape?.shape === 'path'
        ? 'Drag to move the path. Double-click it to edit nodes and curves.'
        : selectedShape?.callout
          ? 'Callout: drag a corner to resize; drag the pointer tip onto an atom, bond, cycle center or shape to attach. Type or double-click to edit centered text.'
          : selectedShape
            ? 'Center: move. Top handle: rotate. Corners: resize or scale. Shift keeps angles and proportions.'
            : notice,
    rings,
    annotations: preview?.shape
      ? [...annotations.filter((a) => a.id !== preview.shape!.id), preview.shape]
      : annotations,
    // #637 -- while a text block is dragged, the offset every selected annotation previews at.
    annotationOffset: (() => {
      const entry = preview?.textPositions && Object.entries(preview.textPositions)[0];
      const origin = entry && preview?.textOrigins?.[entry[0]];
      return entry && origin ? { x: entry[1].x - origin.x, y: entry[1].y - origin.y } : undefined;
    })(),
    ringLines: preview?.ring?.lines,
    chainLines:
      preview?.chainStart !== undefined ? [...(preview.frozenChain ?? []), ...(preview.chain ?? [])] : undefined,
    // #451: live angle + atom-count readout for the chain tool, anchored near the pointer.
    // `segmentAtomCount` is the current (unfrozen) segment's new-atom count; `totalAtomCount`
    // (1 start atom + every bond drawn so far) only appears once a freeze has happened -- before
    // that it would just repeat the same number.
    chainHud:
      preview?.chainCurrent && preview.chainAngleDegrees !== undefined
        ? {
            point: preview.chainCurrent,
            angleDegrees: preview.chainAngleDegrees,
            segmentAtomCount: preview.chain?.length ?? 0,
            totalAtomCount: preview.frozenChain?.length
              ? 1 + preview.frozenChain.length + (preview.chain?.length ?? 0)
              : undefined
          }
        : undefined
  };
}
